import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";

import authManager from "../../../lib/authManager";
import {
  OPERATIONAL_STATUS,
  PICKUP_IN_PROGRESS_STATUSES,
  RUNNER_IN_HAND_STATUSES,
  SESSIONS_POLL_INTERVAL_MS,
} from "../constants";
import { mapSessionToArrival } from "../helpers";
import { AUDIENCE, primaryActionFor } from "../jobActions";
import { valetParkingQueue, valetRunnerParkingQueue } from "./queries";
import useValetSessionActions from "./useValetSessionActions";
import { friendlyValetError } from "./valetErrors";

const PICKUP_STATUSES = ["REQUESTED", ...PICKUP_IN_PROGRESS_STATUSES];

const JOB_STATUSES = ["CLAIMED", "PARKING", ...PICKUP_STATUSES];

/*
 * The queue works the same row the desk does - mapSessionToArrival already
 * flattens the desk's booking and the runner's narrow projection into one
 * shape - plus what only this runner's view can say about it.
 */
export function toJob(row, userId, carrying = null, vehiclePhotoMode) {
  const kind = PICKUP_STATUSES.includes(row.status) ? "pickup" : "park";
  const runnerId = kind === "pickup" ? row.pickedBy : row.parkedBy;

  const job = {
    ...row,
    kind,
    runnerId,
    runnerName: kind === "pickup" ? row.pickedByName : row.parkedByName,
    mine: Boolean(runnerId) && runnerId === userId,
    statusLabel: OPERATIONAL_STATUS[row.status] ?? row.status,
    /* A card the hotel never charges through us owes nothing at handover. */
    unpaid: row.paymentRequired !== false && row.paid !== true,
  };

  return {
    ...job,
    action: primaryActionFor({
      row: job,
      audience: AUDIENCE.RUNNER,
      carrying,
      vehiclePhotoMode,
    }),
  };
}

export default function useRunnerJobs({
  facilityId,
  onParked,
  onDelivered,
  runnerScoped = false,
  vehiclePhotoMode,
} = {}) {
  const [actionError, setActionError] = useState(null);

  const {
    data,
    error: queueError,
    refetch: refetchParking,
  } = useQuery(runnerScoped ? valetRunnerParkingQueue : valetParkingQueue, {
    variables: { facilityId, limit: 1000 },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
    pollInterval: SESSIONS_POLL_INTERVAL_MS,
  });

  const rawItems = runnerScoped
    ? data?.valetRunnerSessions?.items
    : data?.valetSessions?.items;

  const {
    startParking,
    returnToQueue,
    confirmParked,
    startReturn,
    requestAndStartReturn,
    advancePickup,
    returnPickupToQueue,
    confirmReturned,
    returningToQueue,
    confirmingParked,
    returningPickup,
    confirmingReturned,
    parkingBusy,
  } = useValetSessionActions(facilityId, { runnerScoped });

  const userId = authManager.userId;

  const jobs = useMemo(() => {
    /* In the server's order: whoever has waited longest first, and pickups
     * booked for later at the bottom until their time. */
    const rows = (rawItems ?? [])
      .filter(Boolean)
      .map(mapSessionToArrival)
      .filter((row) => JOB_STATUSES.includes(row.status));

    const carrying =
      rows.find((row) =>
        row.status === "PARKING"
          ? row.parkedBy === userId
          : RUNNER_IN_HAND_STATUSES.includes(row.status) &&
            row.pickedBy === userId,
      ) ?? null;

    return rows.map((row) => toJob(row, userId, carrying, vehiclePhotoMode));
  }, [rawItems, userId, vehiclePhotoMode]);

  const mineCount = useMemo(
    () => jobs.filter((job) => job.mine).length,
    [jobs],
  );

  const run = useCallback(
    async (action, fallback) => {
      setActionError(null);
      try {
        return await action();
      } catch (e) {
        setActionError(friendlyValetError(e, fallback));
        // The status the tablet thought it had is the thing in doubt, so resync.
        refetchParking();
        return null;
      }
    },
    [refetchParking],
  );

  const accept = useCallback(
    (job) =>
      run(
        () =>
          job.kind === "park"
            ? startParking(job.valetSessionId)
            : startReturn(job.valetSessionId),
        "take this car",
      ),
    [run, startParking, startReturn],
  );

  const acceptScannedPickup = useCallback(
    (bookingId) =>
      run(() => requestAndStartReturn(bookingId), "take this pickup"),
    [run, requestAndStartReturn],
  );

  const handBack = useCallback(
    (job) =>
      run(
        () =>
          job.kind === "park"
            ? returnToQueue(job.valetSessionId)
            : returnPickupToQueue(job.valetSessionId),
        "hand this car back",
      ),
    [run, returnToQueue, returnPickupToQueue],
  );

  /* One tap along the pickup ladder. The server refuses a step out of order. */
  const advance = useCallback(
    (job) => {
      const step = job.action?.step;
      if (!step) return Promise.resolve(null);

      return run(
        () => advancePickup(step.mutation, job.valetSessionId),
        `record this car ${step.done}`,
      );
    },
    [run, advancePickup],
  );

  /*
   * The lot and the bay come from the runner standing in the car park - there
   * is no allocation to fall back on. Returns the job, or null if the write
   * failed.
   */
  const confirmPark = useCallback(
    (job, { lotId, bay, offsiteTicket }) =>
      run(async () => {
        await confirmParked(job.valetSessionId, {
          lotId,
          bay: bay?.trim(),
          offsiteTicket: offsiteTicket?.trim(),
        });
        if (onParked) onParked(job, bay);
        return job;
      }, "confirm this car parked"),
    [run, confirmParked, onParked],
  );

  /* Resolves true once the car is with the guest and the session is closed. */
  const confirmDelivery = useCallback(
    async (job) => {
      const result = await run(async () => {
        await confirmReturned(job.valetSessionId);
        if (onDelivered) onDelivered(job);
        return true;
      }, "mark this car delivered");

      return result === true;
    },
    [run, confirmReturned, onDelivered],
  );

  return {
    jobs,
    mineCount,

    accept,
    acceptScannedPickup,
    handBack,
    advance,
    confirmPark,
    confirmDelivery,

    actionError,
    queueError,
    // One pair of flags for the modals, whichever kind of job they hold.
    confirming: confirmingParked || confirmingReturned,
    returning: returningToQueue || returningPickup,
    busy: parkingBusy,
  };
}
